import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Users, CreditCard, Settings, LifeBuoy, LogOut, Menu, Search, Lock, AlertTriangle, Clock, ChevronDown, ChevronRight, Sparkles } from "lucide-react";
import { Sheet, SheetContent } from "../ui/sheet";
import { Logo } from "../Brand";
import { api, fmtDate, setClientOrg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useNestixSessionSync } from "../../nestix/context/AuthContext";
import { MODULES, mergeMenu } from "../../nestix/config/menuConfig";
import nxApi from "../../nestix/services/api";
import { setCompanyName } from "../../nestix/utils/company";
import { ClientSwitcher, NoClients } from "../../pages/app/Consultants";

const link = ({ isActive }) => `flex items-center gap-2.5 px-3 py-1.5 rounded-md text-[13px] transition-colors ${isActive ? "bg-white/10 text-white font-semibold" : "text-slate-300 hover:text-white hover:bg-white/5"}`;

function useMenu() {
  const [menu, setMenu] = useState(() => mergeMenu(null));
  useEffect(() => {
    const load = () => nxApi.get("/menu-config").then((r) => setMenu(mergeMenu(r.data.config))).catch(() => {});
    load();
    window.addEventListener("menu-config-updated", load);
    return () => window.removeEventListener("menu-config-updated", load);
  }, []);
  return menu;
}

function NavItem({ item, role }) {
  const mod = MODULES[item.id];
  if (!mod || item.hidden || (mod.requiresAdmin && role !== "org_admin")) return null;
  const Icon = mod.icon;
  return <NavLink to={mod.path} end={mod.path === "/app"} className={link} data-testid={`side-erp-${item.id}`}><Icon className="w-4 h-4 shrink-0" />{item.label || mod.label}</NavLink>;
}

function SideNav({ role }) {
  const menu = useMenu();
  const loc = useLocation();
  const [open, setOpen] = useState(null);
  useEffect(() => {
    const owner = menu.find((s) => !s.standalone && s.items.some((it) => MODULES[it.id] && loc.pathname.startsWith(MODULES[it.id].path)));
    if (owner) setOpen(owner.id);
  }, [loc.pathname, menu]);
  return (
    <nav className="flex-1 overflow-y-auto px-3 pb-6 space-y-2" data-testid="app-sidebar">
      <NavLink to="/app/ai" className={link} data-testid="side-qordel-ai"><Sparkles className="w-4 h-4 shrink-0 text-sky-400" />QORDEL AI</NavLink>
      {menu.filter((s) => !s.hidden).map((s) => s.standalone ? s.items.map((it) => <NavItem key={it.id} item={it} role={role} />) : (
        <div key={s.id}>
          <button onClick={() => setOpen(open === s.id ? null : s.id)} className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-semibold tracking-[0.16em] uppercase text-slate-400 hover:text-white" data-testid={`side-section-${s.id}`}>
            {s.label}{open === s.id ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
          {open === s.id && <div className="space-y-0.5 mt-1">{s.items.map((it) => <NavItem key={it.id} item={it} role={role} />)}</div>}
        </div>
      ))}
      <div>
        <p className="px-3 mb-1.5 text-[10px] font-semibold tracking-[0.16em] uppercase text-slate-500">Administration</p>
        {["org_admin", "manager"].includes(role) && <NavLink to="/app/users" className={link} data-testid="side-users"><Users className="w-4 h-4" />Users &amp; Licences</NavLink>}
        {role === "org_admin" && <NavLink to="/app/billing" className={link} data-testid="side-billing"><CreditCard className="w-4 h-4" />Billing &amp; Plan</NavLink>}
        {role === "org_admin" && <NavLink to="/app/settings" className={link} data-testid="side-settings"><Settings className="w-4 h-4" />Settings &amp; Data</NavLink>}
        <NavLink to="/app/support" className={link} data-testid="side-support"><LifeBuoy className="w-4 h-4" />Support</NavLink>
      </div>
    </nav>
  );
}

function StatusBanner({ access, role }) {
  if (!access) return null;
  const { state, days_left, ends_at, lock_at, payment_failed, kind } = access;
  const cta = role === "org_admin" ? <Link to="/app/billing" className="ml-auto shrink-0 font-semibold underline" data-testid="banner-billing-link">{state === "trial" ? "Choose a plan" : "Renew Subscription"}</Link> : null;
  let cls = "", icon = Clock, text = "";
  if (state === "trial") { cls = "bg-sky-50 text-sky-900 border-sky-200"; text = `Free trial — ${days_left} day${days_left === 1 ? "" : "s"} left (ends ${fmtDate(ends_at)}).`; }
  else if (state === "read_only") { cls = "bg-amber-50 text-amber-900 border-amber-200"; icon = AlertTriangle; text = `${kind === "trial" ? "Your free trial has ended" : "Your subscription has expired"}. Records are read-only. Access will be locked on ${fmtDate(lock_at)} unless renewed.`; }
  else if (payment_failed) { cls = "bg-red-50 text-red-900 border-red-200"; icon = AlertTriangle; text = "Your latest payment failed. Please update your payment method to avoid interruption."; }
  else return null;
  const Icon = icon;
  return <div className={`flex items-center gap-3 px-4 sm:px-6 py-2.5 text-sm border-b ${cls}`} data-testid={`status-banner-${state}`}><Icon className="w-4 h-4 shrink-0" /><span>{text}</span>{cta}</div>;
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const nav = useNavigate();
  useEffect(() => {
    if (q.trim().length < 2) {
      setRes([]);
      return;
    }
    const t = setTimeout(() => api.get("/search", { params: { q } }).then((r) => setRes(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative w-full max-w-md">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search all records…" className="field pl-9 bg-slate-50" data-testid="global-search-input" />
      {res.length > 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-80 overflow-y-auto" data-testid="global-search-results">
          {res.map((r) => (
            <button key={r.id} onClick={() => { setQ(""); nav(`/app/m/${r.module}?open=${r.id}`); }} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm flex gap-3">
              <span className="font-mono text-xs text-slate-500 w-20 shrink-0">{r.ref}</span><span className="truncate">{r.title}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LockedScreen({ access, role }) {
  const suspended = access.state === "suspended";
  return (
    <div className="max-w-xl mx-auto mt-16 border border-slate-200 rounded-xl p-8 bg-white" data-testid="locked-screen">
      <Lock className="w-8 h-8 text-slate-700" />
      <h1 className="mt-4 text-2xl font-bold">{suspended ? "This account is suspended" : "This account is locked"}</h1>
      <p className="mt-3 text-slate-600">{suspended ? "Access has been suspended by QORDEL 360. Your data is protected and has not been deleted. Please contact support." : "This organisation has been inactive for more than 60 days. Your data is protected and has not been deleted. Renew your subscription to restore access."}</p>
      <div className="mt-6 flex gap-3">
        {!suspended && role === "org_admin" && <Link to="/app/billing" className="bg-sky-600 text-white font-semibold px-5 py-2.5 rounded-md" data-testid="locked-reactivate-btn">Reactivate subscription</Link>}
        <Link to="/app/support" className="border border-slate-300 font-semibold px-5 py-2.5 rounded-md">Contact support</Link>
      </div>
      {!suspended && role !== "org_admin" && <p className="mt-4 text-sm text-slate-500">Please ask your organisation administrator to renew the subscription.</p>}
    </div>
  );
}

export default function AppLayout() {
  const { session, logout, refresh } = useAuth();
  const [modules, setModules] = useState([]);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  useNestixSessionSync();
  useEffect(() => {
    api.get("/modules").then((r) => setModules(r.data));
  }, []);
  useEffect(() => {
    setOpen(false);
    refresh();
  }, [loc.pathname, refresh]);
  const { user, org, access } = session;
  setCompanyName(org?.name);
  setClientOrg(user.role === "consultant" ? org?.id : "");
  if (user.role === "consultant" && !org) return <NoClients />;
  const path = loc.pathname.replace(/\/+$/, "") || "/";
  if (user.role === "consultant" && (["/app/users", "/app/billing", "/app/settings", "/data-migration"].includes(path) || path === "/settings" || path.startsWith("/settings/"))) return <Navigate to="/app" replace />;
  const blocked = !user.support && ["locked", "suspended"].includes(access?.state) && !["/app/billing", "/app/support", "/app/settings"].includes(loc.pathname);
  const erpPage = !loc.pathname.startsWith("/app") || loc.pathname === "/app" || loc.pathname === "/app/";
  const sidebar = (
    <div className="flex flex-col h-full bg-[#0A192F]">
      <div className="h-16 flex items-center px-5 shrink-0"><Link to="/app"><Logo light size={28} /></Link></div>
      <div className="px-5 pb-4"><p className="text-xs text-slate-400 truncate" data-testid="sidebar-org-name">{org?.name}</p><p className="font-mono text-[10px] text-slate-500">{org?.tenant_id}</p></div>
      <ClientSwitcher />
      <SideNav role={user.role} />
    </div>
  );
  return (
    <div className="min-h-screen bg-slate-50 lg:pl-64">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64">{sidebar}</aside>
      <Sheet open={open} onOpenChange={setOpen}><SheetContent side="left" className="p-0 w-72 border-0">{sidebar}</SheetContent></Sheet>
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 no-print">
        <div className="h-16 flex items-center gap-4 px-4 sm:px-6">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="app-mobile-menu-btn" aria-label="Menu"><Menu /></button>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-3">
            <div className="hidden sm:block text-right"><p className="text-sm font-semibold text-slate-900" data-testid="header-user-name">{user.name}</p><p className="text-xs text-slate-500 capitalize">{user.role.replace("_", " ")}</p></div>
            <button onClick={async () => { await logout(); nav("/login"); }} className="p-2 rounded-md hover:bg-slate-100" title="Log out" data-testid="logout-btn"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
        <StatusBanner access={access} role={user.role} />
        {(loc.pathname === "/app" || loc.pathname === "/app/") && <Link to="/app/ai" className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 bg-[#0A192F] hover:bg-sky-700 text-white text-sm font-semibold px-4 py-3 rounded-full shadow-lg transition-colors" data-testid="dashboard-ask-ai-btn"><Sparkles className="w-4 h-4 text-sky-300" />Ask QORDEL AI</Link>}
        {user.support && <div className="flex items-center gap-3 px-4 sm:px-6 py-2 text-sm bg-fuchsia-700 text-white" data-testid="support-mode-banner"><span>Support visit: you are inside <b>{user.support.org_name}</b> as QORDEL support. Changes are recorded in their audit log.</span><button onClick={async () => { await api.post("/auth/support/exit"); await refresh(); nav("/admin"); }} className="ml-auto shrink-0 font-semibold underline" data-testid="support-exit-btn">Exit support visit</button></div>}
      </header>
      <main className={erpPage ? "p-0" : "p-4 sm:p-6 lg:p-8"}>{blocked ? <LockedScreen access={access} role={user.role} /> : <Outlet context={{ modules }} />}</main>
    </div>
  );
}
